import { type DatasourceResponseSchemaProperty } from '@atlaskit/linking-types';
import Select, { type SelectProps } from '@databases/atlaskit/Select';
import { DisplayedDetailType } from '@databases/types/database';
import React, { type FunctionComponent, useMemo } from 'react';

import { getCompactSelectStyles } from '../compact-select-styles.ts';

export type PropertyPickerOption = Pick<
  DatasourceResponseSchemaProperty,
  'key' | 'type' | 'title'
>;

const getOptionValue = (option: PropertyPickerOption) => option.key;
const getOptionLabel = (option: PropertyPickerOption) => option.title;
const compactStyles = getCompactSelectStyles<PropertyPickerOption>();

const displayedDetailTypeMap: Record<
  DisplayedDetailType,
  'createdAt' | 'createdBy' | 'updatedAt' | 'updatedBy' | 'excerpt' | 'versionNumber'
> = {
  [DisplayedDetailType.CREATED_AT]: 'createdAt',
  [DisplayedDetailType.CREATED_BY]: 'createdBy',
  [DisplayedDetailType.LAST_UPDATED_AT]: 'updatedAt',
  [DisplayedDetailType.LAST_UPDATED_BY]: 'updatedBy',
  [DisplayedDetailType.EXCERPT]: 'excerpt',
  [DisplayedDetailType.CURRENT_VERSION]: 'versionNumber'
};

interface PropertyPickerProps {
  displayedDetail: DisplayedDetailType | undefined;
  isDisabled?: boolean;
  isLoading?: boolean;
  onChange: (contentField: string) => void;
  options: PropertyPickerOption[] | undefined;
  spacing?: SelectProps<PropertyPickerOption>['spacing'];
  value: string | undefined;
}

const PropertyPicker: FunctionComponent<PropertyPickerProps> = ({
  displayedDetail,
  isDisabled = false,
  isLoading = false,
  onChange,
  options,
  spacing,
  value
}) => {
  const availableOptions = useMemo(() => {
    if (!options) {
      return [];
    }

    return [
      ...options,
      {
        key: 'excerpt',
        title: 'Excerpt',
        type: 'string'
      } as PropertyPickerOption
    ]
      .filter((field) => !['labels', 'status', 'type'].includes(field.key))
      .sort((a, b) => a.title.localeCompare(b.title));
  }, [options]);

  const selectedProperty =
    value ?? (displayedDetail ? displayedDetailTypeMap[displayedDetail] : undefined);

  const resolvedValue = selectedProperty
    ? availableOptions.find((field) => field.key === selectedProperty)
    : undefined;

  const handleChange = (option: PropertyPickerOption | null) => {
    if (option) {
      onChange(option.key);
    }
  };

  return (
    <Select<PropertyPickerOption>
      options={availableOptions}
      value={resolvedValue}
      onChange={handleChange}
      isSearchable={false}
      isDisabled={isDisabled || isLoading}
      isLoading={isLoading}
      getOptionValue={getOptionValue}
      getOptionLabel={getOptionLabel}
      spacing={spacing}
      styles={spacing === 'compact' ? compactStyles : undefined}
    />
  );
};

export default PropertyPicker;
